import { replaceLabel } from "@/components/Markdown/parse";
import { useToast } from "@/composables/toast";
import { useRefreshFromStore } from "@/stores/refreshFromStore";
import { LazyUndoRedoAction, UndoRedoAction } from "@/stores/undoRedoStore";
import { assertDefined } from "@/utils/assertions";
import { cloneStepWithUniqueLabel, getLabelSet } from "./cloneStep";
export class LazyMutateStepAction extends LazyUndoRedoAction {
    get name() {
        var _a;
        return (_a = this.internalName) !== null && _a !== void 0 ? _a : "modify step";
    }
    set name(name) {
        this.internalName = name;
    }
    constructor(stepStore, stepId, key, fromValue, toValue) {
        super();
        Object.defineProperty(this, "key", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "fromValue", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "toValue", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "stepId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "stepStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "onUndoRedo", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.stepStore = stepStore;
        this.stepId = stepId;
        this.key = key;
        this.fromValue = fromValue;
        this.toValue = toValue;
    }
    queued() {
        this.stepStore.updateStepValue(this.stepId, this.key, this.toValue);
    }
    changeValue(value) {
        this.toValue = value;
        this.stepStore.updateStepValue(this.stepId, this.key, this.toValue);
    }
    undo() {
        var _a;
        this.stepStore.updateStepValue(this.stepId, this.key, this.fromValue);
        (_a = this.onUndoRedo) === null || _a === void 0 ? void 0 : _a.call(this);
    }
    redo() {
        var _a;
        this.stepStore.updateStepValue(this.stepId, this.key, this.toValue);
        (_a = this.onUndoRedo) === null || _a === void 0 ? void 0 : _a.call(this);
    }
}
function onLabelSet(classInstance, from, to) {
    var _a;
    const markdown = (_a = classInstance.stateStore.report.markdown) !== null && _a !== void 0 ? _a : "";
    const newMarkdown = replaceLabel(markdown, classInstance.labelType, from, to);
    if (markdown !== newMarkdown) {
        classInstance.stateStore.report.markdown = newMarkdown;
        classInstance.success(`${classInstance.labelTypeTitle} label updated from "${from}" to "${to}" in workflow report.`);
    }
}
export class LazySetLabelAction extends LazyMutateStepAction {
    constructor(stepStore, stateStore, stepId, fromValue, toValue) {
        super(stepStore, stepId, "label", fromValue, toValue);
        Object.defineProperty(this, "labelType", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "labelTypeTitle", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "stateStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "success", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        const step = this.stepStore.getStep(this.stepId);
        assertDefined(step);
        const stepType = step.type;
        const isInput = ["data_input", "data_collection_input", "parameter_input"].indexOf(stepType) >= 0;
        this.labelType = isInput ? "input" : "step";
        this.labelTypeTitle = isInput ? "Input" : "Step";
        this.stateStore = stateStore;
        this.success = useToast().success;
    }
    run() {
        onLabelSet(this, this.fromValue, this.toValue);
    }
    undo() {
        super.undo();
        onLabelSet(this, this.toValue, this.fromValue);
    }
    redo() {
        super.redo();
        this.run();
    }
}
export class LazySetOutputLabelAction extends LazyMutateStepAction {
    constructor(stepStore, stateStore, stepId, fromValue, toValue, toOutputs) {
        const step = stepStore.getStep(stepId);
        assertDefined(step);
        const fromOutputs = structuredClone(step.workflow_outputs);
        super(stepStore, stepId, "workflow_outputs", fromOutputs, structuredClone(toOutputs));
        Object.defineProperty(this, "success", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "fromLabel", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "toLabel", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "stateStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "labelType", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: "output"
        });
        Object.defineProperty(this, "labelTypeTitle", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: "Output"
        });
        this.fromLabel = fromValue;
        this.toLabel = toValue;
        this.stateStore = stateStore;
        this.success = useToast().success;
    }
    run() {
        onLabelSet(this, this.fromLabel, this.toLabel);
    }
    undo() {
        super.undo();
        onLabelSet(this, this.toLabel, this.fromLabel);
    }
    redo() {
        this.run();
        super.redo();
    }
}
export class UpdateStepAction extends UndoRedoAction {
    get name() {
        var _a;
        return (_a = this.internalName) !== null && _a !== void 0 ? _a : "modify step";
    }
    set name(name) {
        this.internalName = name;
    }
    constructor(stepStore, stateStore, stepId, fromPartial, toPartial) {
        super();
        Object.defineProperty(this, "stepStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "stateStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "stepId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "fromPartial", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "toPartial", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "onUndoRedo", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.stepStore = stepStore;
        this.stateStore = stateStore;
        this.stepId = stepId;
        this.fromPartial = fromPartial;
        this.toPartial = toPartial;
    }
    isEmpty() {
        return Object.keys(this.fromPartial).length === 0;
    }
    run() {
        const step = this.stepStore.getStep(this.stepId);
        assertDefined(step);
        this.stateStore.activeNodeId = this.stepId;
        this.stepStore.updateStep(Object.assign(Object.assign({}, step), this.toPartial));
    }
    undo() {
        var _a;
        const step = this.stepStore.getStep(this.stepId);
        assertDefined(step);
        this.stateStore.activeNodeId = this.stepId;
        this.stepStore.updateStep(Object.assign(Object.assign({}, step), this.fromPartial));
        (_a = this.onUndoRedo) === null || _a === void 0 ? void 0 : _a.call(this);
    }
    redo() {
        var _a;
        this.run();
        (_a = this.onUndoRedo) === null || _a === void 0 ? void 0 : _a.call(this);
    }
}
export class SetDataAction extends UpdateStepAction {
    constructor(stepStore, stateStore, from, to) {
        const fromPartial = {};
        const toPartial = {};
        Object.entries(from).forEach(([key, value]) => {
            const otherValue = to[key];
            if (JSON.stringify(value) !== JSON.stringify(otherValue)) {
                fromPartial[key] = structuredClone(value);
                toPartial[key] = structuredClone(otherValue);
            }
        });
        super(stepStore, stateStore, from.id, fromPartial, toPartial);
    }
}
export class InsertStepAction extends UndoRedoAction {
    constructor(stepStore, stateStore, stepData) {
        super();
        Object.defineProperty(this, "stepStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "stateStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "stepData", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "updateStepData", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "stepId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "newStepData", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.stepStore = stepStore;
        this.stateStore = stateStore;
        this.stepData = stepData;
    }
    get name() {
        return `insert ${this.stepData.name}`;
    }
    stepDataToTuple() {
        return Object.values(this.stepData);
    }
    getNewStepData() {
        assertDefined(this.newStepData);
        return this.newStepData;
    }
    run() {
        this.newStepData = this.stepStore.insertNewStep(...this.stepDataToTuple());
        this.stepId = this.newStepData.id;
        if (this.updateStepData) {
            this.stepStore.updateStep(this.updateStepData);
            this.stepId = this.updateStepData.id;
        }
    }
    undo() {
        assertDefined(this.stepId);
        this.stepStore.removeStep(this.stepId);
    }
    redo() {
        this.run();
        assertDefined(this.stepId);
        this.stateStore.activeNodeId = this.stepId;
    }
}
export class RemoveStepAction extends UndoRedoAction {
    constructor(stepStore, stateStore, connectionStore, showAttributesCallback, step) {
        super();
        Object.defineProperty(this, "stepStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "stateStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "connectionStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "showAttributesCallback", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "step", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "connections", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.stepStore = stepStore;
        this.stateStore = stateStore;
        this.connectionStore = connectionStore;
        this.showAttributesCallback = showAttributesCallback;
        this.step = structuredClone(step);
        this.connections = structuredClone(this.connectionStore.getConnectionsForStep(this.step.id));
    }
    get name() {
        var _a;
        return `remove ${(_a = this.step.label) !== null && _a !== void 0 ? _a : this.step.name}`;
    }
    run() {
        this.stepStore.removeStep(this.step.id);
        this.showAttributesCallback();
        this.stateStore.hasChanges = true;
    }
    undo() {
        this.stepStore.addStep(structuredClone(this.step), false, false);
        this.connections.forEach((connection) => this.connectionStore.addConnection(connection));
        this.stateStore.activeNodeId = this.step.id;
        this.stateStore.hasChanges = true;
    }
}
export class CopyStepAction extends UndoRedoAction {
    constructor(stepStore, stateStore, step) {
        super();
        Object.defineProperty(this, "stepStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "stateStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "step", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "stepId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "onUndoRedo", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.stepStore = stepStore;
        this.stateStore = stateStore;
        const labelSet = getLabelSet(stepStore);
        this.step = cloneStepWithUniqueLabel(step, labelSet);
        delete this.step.id;
    }
    get name() {
        var _a;
        return `duplicate step ${(_a = this.step.label) !== null && _a !== void 0 ? _a : this.step.name}`;
    }
    run() {
        const newStep = this.stepStore.addStep(structuredClone(this.step));
        this.stepId = newStep.id;
        this.stateStore.activeNodeId = this.stepId;
        this.stateStore.hasChanges = true;
    }
    undo() {
        assertDefined(this.stepId);
        this.stepStore.removeStep(this.stepId);
    }
}
export class ToggleStepSelectedAction extends UndoRedoAction {
    constructor(stateStore, stepStore, stepId) {
        super();
        Object.defineProperty(this, "stateStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "stepStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "stepId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "toggleTo", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.stateStore = stateStore;
        this.stepStore = stepStore;
        this.stepId = stepId;
        this.toggleTo = !this.stateStore.getStepMultiSelected(stepId);
    }
    get stepLabel() {
        var _a;
        const label = (_a = this.stepStore.getStep(this.stepId)) === null || _a === void 0 ? void 0 : _a.label;
        return label !== null && label !== void 0 ? label : `${this.stepId + 1}`;
    }
    get name() {
        if (this.toggleTo === true) {
            return `add step ${this.stepLabel} to selection`;
        }
        else {
            return `remove step ${this.stepLabel} from selection`;
        }
    }
    run() {
        this.stateStore.setStepMultiSelected(this.stepId, this.toggleTo);
    }
    undo() {
        this.stateStore.setStepMultiSelected(this.stepId, !this.toggleTo);
    }
}
export function useStepActions(stepStore, undoRedoStore, stateStore, connectionStore) {
    /**
     * If the pending action is a `LazyMutateStepAction` and matches the step id and field key, returns it.
     * Otherwise returns `null`
     */
    function actionForIdAndKey(id, key) {
        const pendingAction = undoRedoStore.pendingLazyAction;
        if (pendingAction instanceof LazyMutateStepAction && pendingAction.stepId === id && pendingAction.key === key) {
            return pendingAction;
        }
        else {
            return null;
        }
    }
    /**
     * Mutates a queued lazy action, if a matching one exists,
     * otherwise creates a new lazy action ans queues it.
     */
    function changeValueOrCreateAction(options) {
        var _a;
        const { step, key, value, name, keepActionAlive, timeout } = options;
        const actionForKey = actionForIdAndKey(step.id, key);
        if (actionForKey) {
            actionForKey.changeValue(value);
            if (keepActionAlive) {
                undoRedoStore.setLazyActionTimeout(timeout);
            }
            return actionForKey;
        }
        else {
            const actionConstructor = (_a = options.actionConstructor) !== null && _a !== void 0 ? _a : (() => new LazyMutateStepAction(stepStore, step.id, key, step[key], value));
            const action = actionConstructor();
            if (name) {
                action.name = name;
            }
            undoRedoStore.applyLazyAction(action, timeout);
            action.onUndoRedo = () => {
                stateStore.activeNodeId = step.id;
                stateStore.hasChanges = true;
            };
            return action;
        }
    }
    function setPosition(step, position) {
        changeValueOrCreateAction({ step, key: "position", value: position, name: "change step position" });
    }
    function setAnnotation(step, annotation) {
        changeValueOrCreateAction({ step, key: "annotation", value: annotation, name: "modify step annotation" });
    }
    function setOutputLabel(step, workflowOutputs, fromLabel, toLabel) {
        const actionConstructor = () => new LazySetOutputLabelAction(stepStore, stateStore, step.id, fromLabel, toLabel, workflowOutputs);
        changeValueOrCreateAction({
            step,
            key: "workflow_outputs",
            value: workflowOutputs,
            name: "modify step output label",
            actionConstructor,
            keepActionAlive: true,
            timeout: 2000,
        });
    }
    function setLabel(step, label) {
        const actionConstructor = () => new LazySetLabelAction(stepStore, stateStore, step.id, step.label, label);
        changeValueOrCreateAction({
            step,
            key: "label",
            value: label,
            name: "modify step label",
            actionConstructor,
            keepActionAlive: true,
            timeout: 2000,
        });
    }
    const { refresh } = useRefreshFromStore();
    function setData(from, to) {
        const action = new SetDataAction(stepStore, stateStore, from, to);
        if (!action.isEmpty()) {
            action.onUndoRedo = () => {
                stateStore.activeNodeId = from.id;
                stateStore.hasChanges = true;
                refresh();
            };
            undoRedoStore.applyAction(action);
        }
    }
    function removeStep(step, showAttributesCallback) {
        const action = new RemoveStepAction(stepStore, stateStore, connectionStore, showAttributesCallback, step);
        undoRedoStore.applyAction(action);
    }
    function updateStep(id, toPartial) {
        const fromStep = stepStore.getStep(id);
        assertDefined(fromStep);
        const fromPartial = {};
        Object.keys(toPartial).forEach((key) => {
            fromPartial[key] = structuredClone(fromStep[key]);
        });
        const action = new UpdateStepAction(stepStore, stateStore, id, fromPartial, toPartial);
        if (!action.isEmpty()) {
            action.onUndoRedo = () => {
                stateStore.activeNodeId = id;
                stateStore.hasChanges = true;
                refresh();
            };
            undoRedoStore.applyAction(action);
        }
    }
    function copyStep(step) {
        const action = new CopyStepAction(stepStore, stateStore, step);
        undoRedoStore.applyAction(action);
    }
    return {
        setPosition,
        setAnnotation,
        setLabel,
        setData,
        setOutputLabel,
        removeStep,
        updateStep,
        copyStep,
    };
}
